import React, {Suspense, useEffect, useState} from "react";
import ErrorBoundary from "../../../utils/ErrorBoundary";
import {ButtonPropType} from "../paypal/helpers/paypalTypes";
import ApplePayButtonLoader from "./ApplePayButtonLoader";
import {applePayUspKey} from "../../../cart/CartContextProvider/CartContextProvider";
import {getShopId} from "../../getShopId";

const ApplePayButton: React.FC<Omit<ButtonPropType, "isExpress">> = (props) => (
  <ErrorBoundary fallback={null} handleError={props.handleError} occurrence="Apple Pay Button">
    <Suspense fallback={null}>
      <ApplePayButtonValidator {...props} />
    </Suspense>
  </ErrorBoundary>
);

export default ApplePayButton;

const ApplePayButtonValidator: React.FC<Omit<ButtonPropType, "isExpress">> = (props) => {
  const [supportedVersion, setSupportedVersion] = useState<number>(0);

  useEffect(() => {
    // Activation by url param, for company internal testing
    // See: CartContextProvider.tsx, region: 'Look for url testing param'
    const activeByUrlParam = __DEVELOPMENT__ || sessionStorage.getItem(applePayUspKey);

    // Killswitch, added for fast deactivation, if something is
    // completely going wrong.  _See: publishIncludesNavigation.xsl_
    const hiddenByKillswitch = document.body
      .querySelector<HTMLElement>("nav > ul[data-hide-apple-pay]")
      ?.dataset.hideApplePay?.includes(`${getShopId()}`);

    if (!activeByUrlParam && hiddenByKillswitch) {
      return;
    }

    let tempSupportedVersion = 0;

    // - Is a braintree client given
    // - Are we in Safari (has _global_ `ApplePaySession` ctor)
    // - Does device support Apple Pay
    // - Are necessary version numbers supported (beginning with the lowest)
    if (
      !window.ApplePaySession ||
      !ApplePaySession.canMakePayments() ||
      ![3, 4, 5, 6].some((version) => {
        // https://developer.apple.com/documentation/apple_pay_on_the_web/applepaysession/1778014-supportsversion#parameters
        if (ApplePaySession.supportsVersion(version)) {
          tempSupportedVersion = version;

          return true;
        } else {
          return false;
        }
      })
    ) {
      return;
    }

    setSupportedVersion(tempSupportedVersion);
  }, []);

  if (!supportedVersion) {
    return null;
  }

  return <ApplePayButtonLoader {...props} supportedVersion={supportedVersion} />;
};
